/**
 * Knowledge Base REST API client
 *
 * Auth: Chainlit sets an httponly cookie (`access_token`) on login.
 * Same-origin fetch with credentials:'include' sends it automatically.
 */

const BASE = '/api/kb';

const req = (method: string, path: string, body?: unknown, isMultipart = false) =>
  fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: isMultipart ? undefined : { 'Content-Type': 'application/json' },
    body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
  }).then(async (res) => {
    if (!res.ok) {
      const text = await res.text().catch(() => res.statusText);
      throw new Error(text || `HTTP ${res.status}`);
    }
    if (res.status === 204) return undefined;
    return res.json();
  });

export interface KnowledgeBase {
  id: string;
  name: string;
  description: string;
  chunk_size: number;
  file_count: number;
  created_at: string;
}

export interface KBFile {
  id: string;
  file_name: string;
  /** 'processing' | 'ready' | 'error' */
  status: string;
  total_chunks: number;
  total_tokens: number;
  created_at: string;
}

export interface KBDetail extends KnowledgeBase {
  files: KBFile[];
}

export const kbApi = {
  list: (): Promise<KnowledgeBase[]> =>
    req('GET', ''),

  create: (name: string, description: string, chunk_size: number): Promise<KnowledgeBase> =>
    req('POST', '', { name, description, chunk_size }),

  get: (id: string): Promise<KBDetail> =>
    req('GET', `/${id}`),

  delete: (id: string): Promise<void> =>
    req('DELETE', `/${id}`),

  uploadDocs: (kbId: string, files: File[]): Promise<KBFile[]> => {
    const form = new FormData();
    files.forEach((f) => form.append('files', f));
    return req('POST', `/${kbId}/docs`, form, true);
  },

  deleteDoc: (kbId: string, fileId: string): Promise<void> =>
    req('DELETE', `/${kbId}/docs/${fileId}`),
};
